import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { ISetCamera } from "../../models/interactions/SetCamera";
import { ISequence } from "../../models/interactions/Sequence";
import { InteractionType } from "../../models/interactions/Interaction";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import { sequenceStateDotClassName } from "./sequenceStateDot";
import InteractionPointPreview, { CameraPointStrategy } from "./InteractionPointPreview";
import CameraHoverPreview from "./CameraHoverPreview";
import { getEnvironmentPositionContexts } from "../sequence/EnvironmentPositionContext";
import useSequenceStateOnMap from "./useSequenceStateOnMap";

export default function Viewport3DSequenceCameraPositionLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();
  const sequenceState = useSequenceStateOnMap();

  const activeInteraction = useMemo(() => {
    return sequence?.nodes?.find((node) => node.id === picker.nodeId)?.data
      ?.interaction as ISetCamera | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "camera" &&
    !!activeInteraction &&
    activeInteraction.type === InteractionType.SetCamera;

  const pickableCameraPositions = useMemo(() => {
    return getEnvironmentPositionContexts(
      takePositions ?? [],
      currentTake?.situations,
      getSituationMetadata,
    ).flatMap((context) =>
      context.cameraPositions.map((cameraPosition) => ({
        cameraPosition,
        takeColor: context.takeColor,
        takeLocationId: context.takeLocationId,
      })),
    );
  }, [currentTake?.situations, getSituationMetadata, takePositions]);

  if (!isVisible || !activeInteraction) {
    return null;
  }

  const selectedCameraPositionId = activeInteraction.cameraId;
  // The camera the sequence has active by this node, when that isn't the take's.
  const activeCameraIds = sequenceState?.state.camera?.changed
    ? sequenceState.state.camera.values
    : [];

  return (
    <>
      {pickableCameraPositions.map(
        ({ cameraPosition, takeColor, takeLocationId }) => {
          const isSelected =
            selectedCameraPositionId === cameraPosition.id &&
            (!activeInteraction.takeLocationId ||
              activeInteraction.takeLocationId === takeLocationId);

          return (
            <InteractionPointPreview
              key={`${takeLocationId}/${cameraPosition.id}`}
              strategy={CameraPointStrategy}
              x={cameraPosition.position.x}
              y={cameraPosition.position.y}
              markerBorderColor={takeColor}
              directionDegrees={cameraPosition.direction?.x}
              directionColor={takeColor}
              iconClassName={isSelected ? "sequence-target-rim" : undefined}
              badgeContent={activeCameraIds.includes(cameraPosition.id) ? "" : undefined}
              badgeClassName={sequenceStateDotClassName({
                changed: true,
                uncertain: activeCameraIds.length > 1,
              })}
              tooltipContent={
                <CameraHoverPreview cameraId={cameraPosition.id} name={cameraPosition.name} />
              }
              tooltipDirection="top"
              tooltipClassName="interactionpointpreview-camera-tooltip"
              onClick={() => {
                updateInteraction(picker.nodeId, {
                  ...activeInteraction,
                  cameraId: cameraPosition.id,
                  takeLocationId,
                } as ISetCamera);
                dispatch(closeSequencePositionPicker());
              }}
            />
          );
        },
      )}
    </>
  );
}
